import React, { useRef } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";

/*
 * Shared full-size viewer, portaled straight into document.body so it
 * always sits above the fixed navbar regardless of which layout
 * section renders it (same reasoning as the original Gallery.jsx).
 */
export default function GalleryLightbox({ items, activeIndex, activeItem, onClose, onPrev, onNext }) {
  const touchStartX = useRef(null);

  if (!activeItem) return null;

  // Swipe left/right over the image to move between photos, same as
  // the arrow buttons — the primary way people browse a gallery on a
  // phone is by flicking through it, not tapping small arrows.
  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;
    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    const SWIPE_THRESHOLD = 40;
    if (deltaX > SWIPE_THRESHOLD) onPrev();
    else if (deltaX < -SWIPE_THRESHOLD) onNext();
    touchStartX.current = null;
  };

  return createPortal(
    <div
      className="tp-gallery__lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={activeItem.title}
      onClick={onClose}
    >
      <button className="tp-gallery__lightbox-close" aria-label="Close" onClick={onClose}>
        <Icon name="close" size={22} />
      </button>

      <button
        className="tp-gallery__lightbox-nav tp-gallery__lightbox-nav--prev"
        aria-label="Previous image"
        onClick={(event) => {
          event.stopPropagation();
          onPrev();
        }}
      >
        <Icon name="chevronLeft" size={26} />
      </button>

      <figure
        className="tp-gallery__lightbox-figure"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img src={activeItem.image} alt={activeItem.title} />
        <figcaption>
          <strong>{activeItem.title}</strong>
          <span>
            {activeIndex + 1} / {items.length}
          </span>
        </figcaption>
      </figure>

      <button
        className="tp-gallery__lightbox-nav tp-gallery__lightbox-nav--next"
        aria-label="Next image"
        onClick={(event) => {
          event.stopPropagation();
          onNext();
        }}
      >
        <Icon name="chevronRight" size={26} />
      </button>
    </div>,
    document.body
  );
}
